/**
 * ExpiringQuotesWidget
 * Toont offertes die binnen 7 dagen verlopen (status VERZONDEN).
 * Commercial call-to-action voor opvolging van hoogwaardige leads.
 */

import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, ArrowRight, CheckCircle2 } from 'lucide-react'
import api from '../services/api'

function formatCurrency(value, locale = 'nl-NL') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0)
}

export default function ExpiringQuotesWidget() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'expiring-quotes-stats'],
    queryFn: async () => {
      const response = await api.get('/dashboard/expiring-quotes-stats')
      return response.data
    },
    refetchInterval: 60000,
  })

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow border border-line dark:border-gray-700 p-6 border-l-4 border-gray-300 animate-pulse">
        <div className="h-5 bg-gray-200 rounded w-3/4 mb-4" />
        <div className="h-8 bg-gray-200 rounded w-1/2 mb-2" />
        <div className="h-4 bg-gray-200 rounded w-1/3" />
      </div>
    )
  }

  const totalCount = data?.total_count ?? 0
  const totalValue = data?.total_value_ex_btw ?? 0
  const topQuotes = data?.top_quotes ?? []
  // Offertes die niet berekend konden worden: zichtbaar met de reden van de backend, niet stil
  // weg uit de waarde.
  const nietBerekend = data?.niet_berekend ?? []
  const hasUrgency = totalCount > 0

  return (
    <div
      className={`rounded-lg shadow p-6 border-l-4 ${
        hasUrgency
          ? 'bg-amber-50 border-amber-400 dark:bg-amber-900/20 dark:border-amber-500'
          : 'bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-600'
      }`}
    >
      <div className="flex items-center gap-2 mb-3">
        {hasUrgency ? (
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        ) : (
          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
        )}
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
          {t('dashboard.expiringQuotes.title', { defaultValue: 'Offertes verlopend binnen 7 dagen' })}
        </h2>
      </div>

      {hasUrgency ? (
        <>
          <div className="mb-2">
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(totalValue)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t('dashboard.expiringQuotes.totalValue', { defaultValue: 'Totale waarde (excl. BTW)' })}
            </p>
          </div>
          <p className="text-sm text-amber-700 dark:text-amber-300 font-medium mb-4">
            {t('dashboard.expiringQuotes.quoteCount', { count: totalCount, defaultValue: `${totalCount} offerte(s)` })}
          </p>

          {nietBerekend.length > 0 && (
            <div className="space-y-2 mb-4" data-testid="niet-berekend">
              <p className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase tracking-wide">
                {t('dashboard.expiringQuotes.nietBerekend', { defaultValue: 'Niet berekend' })}
              </p>
              {nietBerekend.map((q) => (
                <button
                  key={q.id}
                  onClick={() => navigate(`/offertes/${q.id}`)}
                  className="w-full text-left p-3 rounded-lg bg-white dark:bg-gray-700/50 border border-red-200 dark:border-red-800"
                >
                  <div className="font-medium text-gray-900 dark:text-white truncate">
                    {q.project_name || '—'}
                  </div>
                  <div className="text-sm text-red-700 dark:text-red-300">
                    {q.reden}
                  </div>
                </button>
              ))}
            </div>
          )}

          {topQuotes.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                {t('dashboard.expiringQuotes.viewQuote', { defaultValue: 'Bekijk offerte' })} →
              </p>
              {topQuotes.map((q) => (
                <button
                  key={q.id}
                  onClick={() => navigate(`/offertes/${q.id}`)}
                  className="w-full text-left p-3 rounded-lg bg-white dark:bg-gray-700/50 border border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:border-amber-300 dark:hover:border-amber-700 transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 dark:text-white truncate">
                        {q.project_name || '—'}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {q.client_name || '—'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <span className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                        {formatCurrency(q.total_price_ex_btw)}
                      </span>
                      <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-amber-600 dark:group-hover:text-amber-400" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="py-6 text-center">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500 dark:text-green-400" />
          <p className="text-gray-600 dark:text-gray-400 font-medium">
            {t('dashboard.expiringQuotes.emptyMessage', { defaultValue: 'Goed gedaan! Alle leads zijn up-to-date.' })}
          </p>
        </div>
      )}
    </div>
  )
}
